import { FieldModel } from '@cibo/core'
import { PaginatedFieldsContext, usePaginatedFields } from '@cibo/landmanager'
import { Loading } from '@cibo/profile'
import {
  FriendlyError,
  LandThumbnail,
  LandThumbnailSkin,
  Markdown,
  ResponsiveDialog,
  useFigures,
} from '@cibo/ui'
import ThumbDownAltRounded from '@mui/icons-material/ThumbDownAltRounded'
import ThumbsUpDownRounded from '@mui/icons-material/ThumbsUpDownRounded'
import {
  Button,
  Checkbox,
  Collapse,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  FormLabel,
  Stack,
  Typography,
} from '@mui/material'
import { GridRowId } from '@mui/x-data-grid-pro'
import { Field, FieldArray, FieldProps, Form, Formik } from 'formik'
import { TextField } from 'formik-mui'
import { filter, includes, map, pipe, sum } from 'ramda'
import { Dispatch, SetStateAction, useContext, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useAgreement, useUpdateAgreement } from '../queries'
import { AgreementDetailProps, AgreementStatus } from '../types'

export type DeclineDialogState = { open: boolean; action?: AgreementStatus }

export const OfferDeclineDialog = ({
  agreementId,
  declineDialogState,
  setDeclineDialogState,
  onSuccess,
}: {
  declineDialogState: DeclineDialogState
  setDeclineDialogState: Dispatch<SetStateAction<DeclineDialogState>>
  onSuccess: (agreeFields: boolean) => void
} & Pick<AgreementDetailProps, 'agreementId'>) => {
  const { t } = useTranslation([
    'impact/pages/Agreements/OfferDeclineDialog',
    'impact/pages/Agreements',
  ])
  const agreement = useAgreement(agreementId)
  const { mutateAsync: updateAgreement } = useUpdateAgreement()
  const { acres } = useFigures()
  const fieldSearch = usePaginatedFields(0, 1000, {
    agreementId: agreementId,
    includeAllResolved: true,
  })
  const { setSelectedRowIds, selectedRowIds } = useContext(PaginatedFieldsContext)
  const [sameForAll, setSameForAll] = useState<boolean>(false)
  const [error, setError] = useState<string>()

  useEffect(() => {
    if (agreement.isFetched && fieldSearch.isFetched) {
      const eligibleWorkflowIds = fieldSearch.data?.items
        ?.map(field => field.id)
        .filter(item => item !== undefined)
      setSelectedRowIds(eligibleWorkflowIds as GridRowId[])
    }
  }, [agreement.isFetched, fieldSearch.isFetched])

  const payment = useMemo(() => {
    if (!fieldSearch.data || selectedRowIds.length < 0) {
      return '--'
    }

    return pipe(
      filter<FieldModel, 'array'>(item => includes(item.id, selectedRowIds)),
      map(i => i.offerPayment()),
      sum
    )(fieldSearch.data.items)
  }, [selectedRowIds, fieldSearch.isFetched])

  const declineFields = useMemo(
    () =>
      fieldSearch.data
        ? declineDialogState.action === 'declined'
          ? fieldSearch.data.items
          : filter(item => !includes(item.id, selectedRowIds), fieldSearch.data.items)
        : [],
    [selectedRowIds, fieldSearch.isFetched, declineDialogState]
  )
  const formInitialValues = useMemo(() => ({ declined: declineFields.map(f => ({ id: f.id })) }), [
    declineFields,
  ])

  if (agreement.isLoading) {
    return <Loading />
  }

  const onSubmitDecline = (formValues: any) => {
    let declined = formValues.declined
    if (formValues.declineAll) {
      declined = declineFields.map(field => ({ id: field.id, reason: formValues.declineAll }))
    }
    updateAgreement({
      agreementId,
      agreedIds: declineDialogState.action === 'agreed' ? (selectedRowIds as string[]) : [],
      status: declineDialogState.action as string,
      declined,
    })
      .then(() =>
        onSuccess(declineDialogState.action === 'agreed' ? selectedRowIds.length > 0 : false)
      )
      .catch(({ response }) => setError(response.data.message))
  }

  return (
    <ResponsiveDialog
      maxWidth="md"
      fullWidth
      open={declineDialogState.open}
      onClose={() => {
        setDeclineDialogState({ open: false, action: undefined })
        setSameForAll(false)
      }}
    >
      <Formik initialValues={formInitialValues} onSubmit={onSubmitDecline}>
        {form => {
          return (
            <Form>
              <DialogTitle>
                {t('finalize', {
                  context: selectedRowIds.length > 0 ? 'mixed' : 'all',
                })}
              </DialogTitle>
              <DialogContent>
                <Stack spacing={1}>
                  <Markdown typographyProps={{ variant: 'body1' }}>
                    {t('explainDeclinedFieldsMd', {
                      context:
                        selectedRowIds.length > 0 && declineDialogState.action !== 'declined'
                          ? 'mixed'
                          : 'all',
                      acceptedFields: selectedRowIds.length,
                      declinedFields: declineFields.length,
                      payment,
                    })}
                  </Markdown>

                  {declineFields.length > 1 && (
                    <FormControlLabel
                      control={<Checkbox onClick={() => setSameForAll(state => !state)} />}
                      value={sameForAll}
                      label={t('sameForAll')}
                    />
                  )}
                  <Collapse in={sameForAll} timeout="auto" unmountOnExit>
                    <Stack>
                      <FormLabel htmlFor="declineAll">{t('reason')}</FormLabel>
                      <Field
                        id="declineAll"
                        name="declineAll"
                        component={TextField}
                        rows={4}
                        multiline
                      />
                    </Stack>
                  </Collapse>
                  <Collapse in={!sameForAll} timeout="auto" unmountOnExit>
                    <Field id="reasons" name="reasons">
                      {({ field }: FieldProps) => (
                        <FieldArray
                          name={'declined'}
                          render={arrayHelpers => {
                            return (
                              <Stack spacing={2} divider={<Divider />}>
                                {declineFields.map((field, index) => (
                                  <Stack direction="row" spacing={2} key={index}>
                                    <LandThumbnail
                                      landProfile={field}
                                      width={60}
                                      height={60}
                                      skin={LandThumbnailSkin.SMALL}
                                    />
                                    <Stack sx={{ minWidth: '200px' }}>
                                      {field.name}
                                      <Typography variant="caption">
                                        {field.county ? `${field.county}, ` : ''}
                                        {field.state ? field.state.toUpperCase() : ''}
                                      </Typography>
                                      <Typography>{acres(field.acres)}</Typography>
                                    </Stack>
                                    <Stack flex={1}>
                                      <FormLabel htmlFor={`${index}`}>{t('reason')}</FormLabel>
                                      <Field
                                        data-testid="decline-dialog-reason"
                                        multiline
                                        rows={2}
                                        id={index}
                                        name={`declined[${index}].reason`}
                                        component={TextField}
                                      />
                                    </Stack>
                                  </Stack>
                                ))}
                              </Stack>
                            )
                          }}
                        />
                      )}
                    </Field>
                  </Collapse>
                </Stack>
                {error && <FriendlyError message={error} />}
              </DialogContent>
              <DialogActions>
                <Button
                  onClick={() => {
                    setDeclineDialogState({ open: false, action: undefined })
                    setSameForAll(false)
                  }}
                >
                  {t('cancel')}
                </Button>
                <Button
                  variant="contained"
                  type="submit"
                  data-testid="decline-dialog-submit"
                  color={declineDialogState.action === 'agreed' ? 'success' : 'error'}
                  endIcon={
                    declineDialogState.action === 'agreed' ? (
                      <ThumbsUpDownRounded />
                    ) : (
                      <ThumbDownAltRounded />
                    )
                  }
                >
                  {t('submit', { context: declineDialogState.action })}
                </Button>
              </DialogActions>
            </Form>
          )
        }}
      </Formik>
    </ResponsiveDialog>
  )
}
